import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Send, AlertCircle, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import PageBanner from './PageBanner';

export default function JoinPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    affiliation: '',
    source: '',
    comments: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone || !formData.affiliation) {
      toast.error('Please fill in all required fields.', {
        style: { borderRadius: '0', background: '#333', color: '#fff' }
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('registrations')
        .insert([
          {
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            affiliation: formData.affiliation,
            source: formData.source,
            comments: formData.comments,
          }
        ]);

      if (error) throw error;

      toast.success('Registration successful!', {
        icon: '🎉',
        style: { borderRadius: '0', background: '#006699', color: '#fff', fontWeight: 'bold' }
      });
      
      // Redirect to thank you page
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Registration failed. Please try again.', {
        style: { borderRadius: '0', background: '#dc2626', color: '#fff' }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafcff] relative overflow-hidden">
      {/* BACKGROUND TEXTURE */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg, rgba(15, 105, 157, 0.02) 0px, rgba(15, 105, 157, 0.02) 1px, transparent 1px, transparent 18px)`,
        }}
      />
      
      <PageBanner 
        title="Join IEEE" 
        description="Become a part of the world's largest technical professional organization for the advancement of technology." 
        Icon={UserPlus} 
      />

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white border border-[#006699]/20 shadow-2xl relative"
        >
          {/* L Corners */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-[#006699] pointer-events-none z-10" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-[#006699] pointer-events-none z-10" />

          {/* Full Width Banner Image */}
          <div className="w-full h-48 sm:h-56 lg:h-72 relative overflow-hidden flex items-center justify-center">
            {/* Blurry Background Image */}
            <img 
              src="/asset/event/anniv6.webp" 
              alt="IEEE Anniversary" 
              className="absolute inset-0 w-full h-full object-cover blur-[2px] scale-105"
            />
            {/* Subtle Overlay for text readability */}
            <div className="absolute inset-0 bg-black/30" />
            
            {/* Text overlay similar to divider styling */}
            <motion.div 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="relative z-10 text-center px-6"
            >
              <p className="text-white text-2xl sm:text-3xl md:text-4xl font-light tracking-[0.15em] uppercase">
                <span className="text-[#7dd3fc] font-medium">#</span> Great Stories Begin Here
              </p>
              <div className="h-[2px] w-16 bg-[#7dd3fc]/50 mx-auto mt-6" />
            </motion.div>
          </div>

          <div className="p-8 sm:p-12 lg:p-16 pt-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Grid for basic info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Full Name */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    value={formData.full_name}
                    onChange={handleChange}
                    className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none"
                    placeholder="+216 55 555 555"
                  />
                </div>

                {/* Affiliation */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center gap-2">
                    Study Affiliation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="affiliation"
                      required
                      value={formData.affiliation}
                      onChange={handleChange}
                      className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Select your affiliation</option>
                      <option value="Preparatory student">Preparatory student</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Bachelor-License">Bachelor-License</option>
                      <option value="Masters">Masters</option>
                    </select>
                    {/* Custom Arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-[#006699]/20 to-transparent my-8" />

              <div className="space-y-8">
                {/* Source */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center justify-between">
                    <span>How did you hear about us?</span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5">OPTIONAL</span>
                  </label>
                  <input
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleChange}
                    className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-3.5 text-sm transition-all duration-300 outline-none"
                    placeholder="e.g. Social Media, Friend, Event..."
                  />
                </div>

                {/* Comments */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#00334d] uppercase tracking-widest flex items-center justify-between">
                    <span>Any question or comment?</span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5">OPTIONAL</span>
                  </label>
                  <textarea
                    name="comments"
                    rows="4"
                    value={formData.comments}
                    onChange={handleChange}
                    className="w-full bg-[#f4f6f9] border border-transparent focus:border-[#006699] focus:bg-white focus:ring-0 rounded-none px-5 py-4 text-sm transition-all duration-300 outline-none resize-none custom-scrollbar"
                    placeholder="Write your message here..."
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#006699] hover:bg-[#00334d] text-white font-black text-sm uppercase tracking-widest py-5 flex items-center justify-center gap-3 transition-all duration-300 rounded-none group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>Submit Application</span>
                      <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </>
                  )}
                </button>
                <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
                  <AlertCircle className="w-4 h-4 text-[#006699] shrink-0" />
                  <p>By submitting this form, you agree to follow the IEEE Code of Ethics and actively participate in branch activities.</p>
                </div>
              </div>

            </form>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
